/**
 * AnsGIS product host — World-style catalog + official AppBridge.
 * geo.* tools/call is intercepted with oncalltool and computed by
 * self-hosted WebR 0.6.0 + sf (engine webr-sf).
 */
import {
  AppBridge,
  getToolUiResourceUri,
  PostMessageTransport,
  RESOURCE_MIME_TYPE,
} from "@modelcontextprotocol/ext-apps/app-bridge";
import {
  Client,
  StreamableHTTPClientTransport,
  type CallToolResult,
  type Tool,
} from "@modelcontextprotocol/client";
import type { Feature, Geometry } from "geojson";
import { HOST_STYLE_VARIABLES } from "./host-styles.ts";
import { initMap, invalidateMap, showGeoOnMap } from "./map.ts";
import {
  computeGeoWebr,
  initWebR,
  webrErrorToTool,
  webrResultToTool,
} from "./webr-geo.ts";
import { CATALOG, type CatalogApp } from "./catalog.ts";
import { ICONS } from "./icons.ts";
import type { GeoComputeResult, GeoToolName } from "../../shared/geo.js";
import "./styles.css";

const IMPLEMENTATION = { name: "AnsGIS Host", version: "0.2.0" };
const GEO_TOOLS = new Set<string>([
  "geo.buffer",
  "geo.centroid",
  "geo.measure",
  "geo.intersect",
]);

type StatusKind = "pending" | "ok" | "err";
type TabId = "apps" | "map" | "host";

const appRoot = document.getElementById("app")!;

appRoot.innerHTML = `
  <div class="shell" id="shell">
    <div class="phone">
      <div class="status-bar"></div>
      <header class="top">
        <div class="brand"><div class="orb" aria-hidden="true"></div>AnsGIS</div>
        <span class="chip" id="webr-chip">WebR…</span>
      </header>
      <div class="search-wrap">
        <label class="search">
          ${ICONS.search}
          <input id="search" type="search" placeholder="Buscar herramientas…" autocomplete="off" />
        </label>
      </div>
      <div class="main">
        <section class="screen active" id="screen-apps" data-tab="apps">
          <div class="hero">
            <h1>Apps</h1>
            <p>Herramientas geo con R en el host. Tocá para abrir. SDK oficial MCP Apps, no MiniKitAns.</p>
          </div>
          <button type="button" class="featured" id="featured">
            <div class="featured-inner">
              <span class="tag">Destacada · WebR + sf</span>
              <h2>Buffer</h2>
              <p>Halo alrededor de un punto. La mini-app solo pide; el host calcula.</p>
            </div>
          </button>
          <div class="sec">
            <div class="sec-h"><h3>Geo · MCP Apps</h3><span>webr-sf</span></div>
            <div class="grid" id="grid"></div>
          </div>
          <div class="sec">
            <div class="sec-h"><h3>Recientes</h3></div>
            <div class="list" id="recent"></div>
          </div>
        </section>
        <section class="screen" id="screen-host" data-tab="host">
          <div class="hero">
            <h1>Host</h1>
            <p>Estado del puente MCP Apps y de WebR 0.6.0.</p>
          </div>
          <div class="host-card">
            <h2>AnsGIS</h2>
            <p class="status-line" id="mcp-status"><span class="dot"></span><strong>MCP:</strong> conectando…</p>
            <p class="status-line" id="webr-status"><span class="dot"></span><strong>WebR:</strong> cargando 0.6.0…</p>
            <p class="status-line" id="hs-status"><span class="dot"></span><strong>Handshake:</strong> la vista aún no está abierta</p>
            <p class="status-line" id="tool-status"><span class="dot"></span><strong>tools/call:</strong> —</p>
          </div>
        </section>
      </div>
      <nav class="tabs">
        <button type="button" class="tab active" data-tab="apps">${ICONS.apps}Apps</button>
        <button type="button" class="tab" data-tab="map">${ICONS.map}Mapa</button>
        <button type="button" class="tab" data-tab="host">${ICONS.host}Host</button>
      </nav>
      <section class="sheet" id="sheet">
        <div class="sheet-bar">
          <button type="button" class="sheet-back" id="sheet-close" aria-label="Cerrar">‹</button>
          <div>
            <div class="sheet-title" id="sheet-title">App</div>
            <div class="sheet-sub" id="sheet-sub">MCP App · tools/call</div>
          </div>
        </div>
        <div class="view-frame-wrap" id="view-frame-wrap"></div>
      </section>
    </div>
    <section class="stage" id="stage">
      <header class="stage-head">
        <div>
          <h2>Mapa</h2>
          <p>El host dibuja el GeoJSON que calcula WebR + sf.</p>
        </div>
      </header>
      <div id="map"></div>
      <div class="map-empty" id="map-empty">Todavía no hay resultado. Abrí una app, calculá, y el mapa se actualiza.</div>
      <aside class="geo-panel">
        <h3>Resultado</h3>
        <pre class="placeholder" id="geo-json">Sin resultado todavía.</pre>
      </aside>
    </section>
  </div>
`;

const shell = document.getElementById("shell")!;
const gridEl = document.getElementById("grid")!;
const recentEl = document.getElementById("recent")!;
const searchEl = document.getElementById("search") as HTMLInputElement;
const sheet = document.getElementById("sheet")!;
const viewWrap = document.getElementById("view-frame-wrap")!;
const geoJsonEl = document.getElementById("geo-json")!;
const mapEmpty = document.getElementById("map-empty")!;
const webrChip = document.getElementById("webr-chip")!;

initMap(document.getElementById("map")!);

const recents: CatalogApp[] = [];

function setLine(prefix: "mcp" | "webr" | "hs" | "tool", kind: StatusKind, text: string) {
  const line = document.getElementById(`${prefix}-status`)!;
  const label =
    prefix === "mcp" ? "MCP" : prefix === "webr" ? "WebR" : prefix === "hs" ? "Handshake" : "tools/call";
  line.innerHTML = `<span class="dot${kind === "ok" ? " ok" : kind === "err" ? " err" : ""}"></span><strong>${label}:</strong> ${text}`;
  if (prefix === "webr") {
    webrChip.textContent =
      kind === "ok" ? "WebR 0.6.0 + sf" : kind === "err" ? "WebR error" : "WebR…";
    webrChip.className = `chip${kind === "ok" ? " ok" : kind === "err" ? " err" : ""}`;
  }
}

function setTab(tab: TabId) {
  for (const screen of document.querySelectorAll(".screen")) {
    screen.classList.toggle("active", (screen as HTMLElement).dataset.tab === tab);
  }
  for (const btn of document.querySelectorAll(".tab")) {
    btn.classList.toggle("active", (btn as HTMLElement).dataset.tab === tab);
  }
  shell.classList.toggle("show-map-mobile", tab === "map");
  if (tab === "map") requestAnimationFrame(() => invalidateMap());
}

function iconFor(slug: string): string {
  if (slug === "buffer") return ICONS.buffer;
  if (slug === "centroid") return ICONS.centroid;
  if (slug === "measure") return ICONS.measure;
  if (slug === "intersect") return ICONS.intersect;
  return ICONS.composer;
}

function renderGrid(filter = "") {
  const q = filter.trim().toLowerCase();
  const items = CATALOG.filter(
    (item) =>
      !q ||
      item.name.toLowerCase().includes(q) ||
      item.desc.toLowerCase().includes(q) ||
      item.id.includes(q),
  );
  gridEl.innerHTML = "";
  if (items.length === 0) {
    gridEl.innerHTML = `<p class="empty">Nada coincide con la búsqueda.</p>`;
    return;
  }
  for (const item of items) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tile";
    btn.dataset.app = item.slug;
    btn.disabled = !item.enabled;
    btn.innerHTML = `<span class="icon">${iconFor(item.slug)}</span><span>${item.name}</span>`;
    if (item.enabled) btn.addEventListener("click", () => void openApp(item));
    gridEl.appendChild(btn);
  }
}

function renderRecents() {
  recentEl.innerHTML = "";
  if (recents.length === 0) {
    recentEl.innerHTML = `<p class="empty" style="padding:18px">Todavía no abriste ninguna app.</p>`;
    return;
  }
  for (const item of recents) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "row";
    btn.innerHTML = `<span class="mini">${iconFor(item.slug)}</span><span class="txt"><strong>${item.name}</strong><small>${item.desc}</small></span><span class="chev">›</span>`;
    btn.addEventListener("click", () => void openApp(item));
    recentEl.appendChild(btn);
  }
}

function remember(item: CatalogApp) {
  const next = [item, ...recents.filter((r) => r.id !== item.id)].slice(0, 5);
  recents.length = 0;
  recents.push(...next);
  renderRecents();
}

searchEl.addEventListener("input", () => renderGrid(searchEl.value));
document.getElementById("featured")!.addEventListener("click", () => {
  const buffer = CATALOG.find((item) => item.id === "geo.buffer");
  if (buffer) void openApp(buffer);
});
document.getElementById("sheet-close")!.addEventListener("click", () => closeSheet());

for (const btn of document.querySelectorAll(".tab")) {
  btn.addEventListener("click", () => setTab((btn as HTMLElement).dataset.tab as TabId));
}

renderGrid();
renderRecents();

let mcpClient: Client | null = null;
let tools = new Map<string, Tool>();
let bridge: AppBridge | null = null;
const viewHtmlCache = new Map<string, string>();

async function connectMcp(): Promise<void> {
  const url = new URL("/mcp", window.location.origin);
  const client = new Client(IMPLEMENTATION);
  await client.connect(new StreamableHTTPClientTransport(url));
  mcpClient = client;
  const listed = await client.listTools();
  tools = new Map(listed.tools.map((tool) => [tool.name, tool]));
  const names = [...tools.keys()].join(", ") || "(ninguna)";
  setLine("mcp", "ok", `conectado · ${names}`);
}

async function loadViewHtml(item: CatalogApp): Promise<string> {
  const cacheKey = item.resourceUri ?? item.viewFile ?? item.id;
  const cached = viewHtmlCache.get(cacheKey);
  if (cached) return cached;

  if (mcpClient && item.tool) {
    const uri =
      getToolUiResourceUri(tools.get(item.tool) ?? {}) ?? item.resourceUri;
    if (uri) {
      const resource = await mcpClient.readResource({ uri });
      const content = resource.contents[0];
      if (!content) throw new Error(`Recurso vacío: ${uri}`);
      if (content.mimeType && content.mimeType !== RESOURCE_MIME_TYPE) {
        throw new Error(`MIME inesperado: ${content.mimeType}`);
      }
      const html =
        "text" in content && typeof content.text === "string"
          ? content.text
          : "blob" in content && typeof content.blob === "string"
            ? atob(content.blob)
            : "";
      if (!html) throw new Error("El recurso UI no trae HTML");
      viewHtmlCache.set(cacheKey, html);
      return html;
    }
  }

  const file = item.viewFile ?? "buffer.html";
  const response = await fetch(`/view/${file}`);
  if (!response.ok) {
    throw new Error("No hay servidor MCP ni dist/view. Ejecutá npm run build && npm start.");
  }
  const html = await response.text();
  viewHtmlCache.set(cacheKey, html);
  return html;
}

function toolResultToFeature(result: CallToolResult): Feature | null {
  const structured = result.structuredContent as GeoComputeResult | undefined;
  if (structured?.feature?.type === "Feature") return structured.feature;

  const block = result.content?.find((item) => item.type === "text");
  if (block && "text" in block) {
    try {
      const parsed = JSON.parse(block.text) as Feature;
      if (parsed?.type === "Feature") return parsed;
    } catch {
      return null;
    }
  }
  return null;
}

function presentResult(result: CallToolResult, extras: Array<Feature | Geometry> = []) {
  if (result.isError) {
    const block = result.content?.find((item) => item.type === "text");
    const text = block && "text" in block ? block.text : "Error en la herramienta";
    setLine("tool", "err", text);
    geoJsonEl.classList.add("placeholder");
    geoJsonEl.textContent = text;
    return;
  }

  const structured = result.structuredContent as GeoComputeResult | undefined;
  const feature = toolResultToFeature(result);
  if (!feature) {
    setLine("tool", "err", "La herramienta no devolvió GeoJSON");
    return;
  }

  showGeoOnMap(feature, structured?.extras ?? extras);
  mapEmpty.classList.add("hidden");
  geoJsonEl.classList.remove("placeholder");
  geoJsonEl.textContent = JSON.stringify(structured ?? feature, null, 2);
  const engine = structured?.engine ?? "mcp";
  const extra =
    structured?.lengthMeters != null
      ? ` · ${structured.lengthMeters.toFixed(1)} m`
      : structured?.areaSqMeters
        ? ` · ${structured.areaSqMeters.toFixed(0)} m²`
        : "";
  setLine("tool", "ok", `listo (${engine})${extra}`);
  requestAnimationFrame(() => invalidateMap());
}

function closeSheet() {
  sheet.classList.remove("open");
}

async function openApp(item: CatalogApp) {
  if (!item.enabled || !item.tool) return;
  remember(item);
  document.getElementById("sheet-title")!.textContent = item.name;
  document.getElementById("sheet-sub")!.textContent = `${item.tool} · MCP App`;
  sheet.classList.add("open");
  viewWrap.replaceChildren();
  setLine("hs", "pending", "inyectando la vista y esperando ui/initialize…");

  if (bridge) {
    try {
      await bridge.teardownResource({});
    } catch {
      /* view may already be gone */
    }
    bridge.onclose = undefined;
    await bridge.close();
    bridge = null;
  }

  let html: string;
  try {
    html = await loadViewHtml(item);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    setLine("hs", "err", message);
    return;
  }

  const iframe = document.createElement("iframe");
  iframe.title = `AnsGIS ${item.name}`;
  iframe.setAttribute("allow", "clipboard-write");
  viewWrap.appendChild(iframe);

  const target = iframe.contentWindow;
  if (!target) {
    setLine("hs", "err", "No hay contentWindow en el iframe");
    return;
  }

  const nextBridge = new AppBridge(
    null,
    IMPLEMENTATION,
    { openLinks: {}, serverTools: {}, logging: {} },
    {
      hostContext: {
        theme: "dark",
        platform: "web",
        styles: { variables: HOST_STYLE_VARIABLES },
        displayMode: "inline",
        availableDisplayModes: ["inline", "fullscreen"],
        containerDimensions: { maxHeight: 2400 },
      },
    },
  );

  nextBridge.oninitialized = () => {
    setLine("hs", "ok", "ui/initialize ↔ initialized");
    nextBridge.sendToolInput({ arguments: item.defaults });
  };

  nextBridge.onloggingmessage = (params) => {
    console.info("[AnsGIS View log]", params);
  };

  nextBridge.onopenlink = async (params) => {
    window.open(params.url, "_blank", "noopener,noreferrer");
    return {};
  };

  nextBridge.onsizechange = async ({ height }) => {
    if (height) iframe.style.minHeight = `${Math.max(280, height)}px`;
  };

  nextBridge.oncalltool = async (params) => {
    setLine("tool", "pending", `tools/call ${params.name}…`);
    const geometry = params.arguments?.geometry as Geometry | undefined;
    const other = params.arguments?.other as Geometry | undefined;
    let result: CallToolResult;
    if (GEO_TOOLS.has(params.name)) {
      try {
        const computed = await computeGeoWebr(
          params.name as GeoToolName,
          params.arguments ?? {},
          (msg) => setLine("webr", "pending", msg),
        );
        setLine("webr", "ok", "0.6.0 + sf · webr-sf (/webr/0.6.0/)");
        result = webrResultToTool(computed);
      } catch (error) {
        setLine(
          "webr",
          "err",
          error instanceof Error ? error.message : "WebR falló",
        );
        result = webrErrorToTool(error);
      }
    } else if (mcpClient) {
      result = (await mcpClient.callTool({
        name: params.name,
        arguments: params.arguments ?? {},
      })) as CallToolResult;
    } else {
      result = {
        isError: true,
        content: [{ type: "text", text: `Herramienta desconocida: ${params.name}` }],
      };
    }
    const extras: Geometry[] = [];
    if (geometry) extras.push(geometry);
    if (other) extras.push(other);
    presentResult(result, extras);
    return result;
  };

  bridge = nextBridge;
  await nextBridge.connect(new PostMessageTransport(target, target));

  const doc = iframe.contentDocument;
  if (!doc) {
    setLine("hs", "err", "No se pudo escribir el HTML de la vista");
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();
}

void (async () => {
  try {
    await connectMcp();
  } catch (error) {
    console.warn("[AnsGIS] MCP HTTP no disponible (geo.* del host no lo necesita)", error);
    setLine("mcp", "err", "sin HTTP · las apps del navegador siguen en WebR + sf");
  }
  try {
    await initWebR((msg) => setLine("webr", "pending", msg));
    setLine("webr", "ok", "0.6.0 + sf · webr-sf (/webr/0.6.0/)");
  } catch (error) {
    console.error("[AnsGIS] WebR init failed", error);
    setLine("webr", "err", error instanceof Error ? error.message : "no arrancó WebR");
  }
})();
